import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FieldRow } from '@/features/store-settings';

import { templateLabelSegment } from '../../model/templateLabels';
import type { ChatbotToolDescriptor } from '../../model/types';

export interface ToolAllowlistFieldProps {
  label: ReactNode;
  description?: ReactNode;
  ariaLabel?: string;
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  tools: ChatbotToolDescriptor[] | undefined;
  /** Dims (not disables) the field when the scope's effective `aiMode !== 'ASSIST'` (design §9.6's
   * dimming pattern, reused here per §17.3.3). */
  dimmed?: boolean;
  /** Fires whenever "Only these tools…" with nothing yet checked starts/stops being true, so the
   * card can disable Save — an empty list means *all* tools, the opposite of a merchant who chose
   * "Only" but has not ticked one yet. */
  onValidityChange?: (valid: boolean) => void;
}

/**
 * Dictionary-fed tool allowlist (design §17.3.3) — replaces the static `TOOL_ALLOWLIST_OPTIONS`
 * `ChipListField`. An empty list means **all** tools (the agent module's own semantics); rendering it
 * as an empty chip field would read as "no tools", the opposite of the truth.
 */
export function ToolAllowlistField({
  label,
  description,
  ariaLabel,
  value,
  onChange,
  disabled = false,
  tools,
  dimmed = false,
  onValidityChange,
}: ToolAllowlistFieldProps) {
  const { t } = useTranslation('chatbot');
  // The RadioGroup's chosen mode is its own state, not purely derived from `value`: an empty array
  // is ambiguous between "All tools" and "Only these tools…, none ticked yet", and deriving it from
  // `value.length` alone made the "Only" mode unreachable (selecting it with nothing checked yet
  // immediately re-derived back to "All", snapping the radio back). `value` still forces `ONLY`
  // whenever it becomes non-empty — that reading is never ambiguous.
  const [uiMode, setUiMode] = useState<'ALL' | 'ONLY'>(value.length === 0 ? 'ALL' : 'ONLY');
  useEffect(() => {
    if (value.length > 0) setUiMode('ONLY');
  }, [value]);
  const mode = uiMode;
  const knownNames = new Set((tools ?? []).map((tool) => tool.name));
  const unknownSelected = value.filter((name) => !knownNames.has(name));
  const onlyModeEmpty = mode === 'ONLY' && value.length === 0;

  useEffect(() => {
    onValidityChange?.(!onlyModeEmpty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyModeEmpty]);

  const setMode = (next: 'ALL' | 'ONLY') => {
    setUiMode(next);
    if (next === 'ALL') onChange([]);
    // Switching to "Only…" checks nothing (design §17.3.3) — the field error blocks Save until
    // at least one tool (or a return to "All") is chosen.
  };

  const toggleTool = (name: string, checked: boolean) => {
    if (checked) onChange([...value, name]);
    else onChange(value.filter((v) => v !== name));
  };

  return (
    <FieldRow label={label} description={description} alignTop>
      <Box sx={{ opacity: dimmed ? 0.6 : 1 }}>
        <RadioGroup
          aria-label={ariaLabel}
          value={mode}
          onChange={(event) => setMode(event.target.value as 'ALL' | 'ONLY')}
        >
          <FormControlLabel
            value="ALL"
            disabled={disabled}
            control={<Radio size="small" />}
            label={t('escalation.tools.modeAll')}
          />
          <FormControlLabel
            value="ONLY"
            disabled={disabled}
            control={<Radio size="small" />}
            label={t('escalation.tools.modeOnly')}
          />
        </RadioGroup>
        {mode === 'ONLY' ? (
          <Box
            role="group"
            aria-label={t('escalation.tools.modeOnly')}
            sx={{
              pl: 4,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              columnGap: 2,
            }}
          >
            <FormGroup>
              {(tools ?? []).map((tool) => (
                <FormControlLabel
                  key={tool.name}
                  control={
                    <Checkbox
                      size="small"
                      checked={value.includes(tool.name)}
                      disabled={disabled}
                      onChange={(event) => toggleTool(tool.name, event.target.checked)}
                    />
                  }
                  label={t(`escalation.tools.items.${templateLabelSegment(tool.name)}`, tool.name)}
                />
              ))}
            </FormGroup>
            <FormGroup>
              {unknownSelected.map((name) => (
                <FormControlLabel
                  key={name}
                  control={
                    <Checkbox
                      size="small"
                      checked
                      disabled={disabled}
                      onChange={() => toggleTool(name, false)}
                    />
                  }
                  label={
                    <Typography
                      variant="caption"
                      color="warning.main"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {t('escalation.tools.unknown', { name })}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
            {onlyModeEmpty ? (
              <FormHelperText error sx={{ gridColumn: '1 / -1' }}>
                {t('escalation.tools.pickOne')}
              </FormHelperText>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
                {t('escalation.tools.noAutoAdd')}
              </Typography>
            )}
          </Box>
        ) : null}
        {dimmed ? (
          <Typography variant="caption" color="text.secondary" component="p">
            {t('escalation.tools.onlyAssist')}
          </Typography>
        ) : null}
      </Box>
    </FieldRow>
  );
}
